import React from 'react';
import NProgress from 'nprogress';
import { Card, formatCurrency, Button, QuantityInput } from '@avila-tek/ui/src';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { Company, Product, Variant, ShopCart } from '../../../models';
import { ADD_PAYROLL_ITEM_TO_SHOP_CART } from '../../../graphql/mutation';
import { useNotify, useShopCart, useUser } from '../../../hooks';
import ProductRating from './ProductRating';
import AddProductModal from './AddProductModal/AddProductModal';
import { VariantOptions } from '../../shared';

interface ProductInfoProps {
  product: Product;
  company: Company;
  selectedVariant: Variant;
  setSelectedVariant: React.Dispatch<React.SetStateAction<Variant>>;
}

function ProductInfo({
  product,
  selectedVariant,
  setSelectedVariant,
  company,
}: ProductInfoProps) {
  const router = useRouter();
  const notify = useNotify();
  const [user] = useUser();
  const [shopCart, setShopcart] = useShopCart();

  const [quantity, setQuantity] = React.useState(1);
  const [modalIsOpen, setModalIsOpen] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);

  const [addPayrollItemToShopCartMutation] = useMutation<{
    addPayrollItemToShopCart: ShopCart;
  }>(ADD_PAYROLL_ITEM_TO_SHOP_CART);

  const addPayrollItemToShopCart = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      e.preventDefault();
      if (disabled) return;
      NProgress.start();
      setDisabled(true);

      if (quantity <= 0)
        return notify('La cantidad debe ser mayor a 0', 'error');

      const { data } = await addPayrollItemToShopCartMutation({
        variables: {
          data: {
            company: (user.company as Company)._id,
            variant: selectedVariant?._id,
            shopCart: shopCart?._id,
            quantity,
          },
        },
      });
      if (data?.addPayrollItemToShopCart._id) {
        notify('Se ha agreagado al carrito con éxito', 'success');
        setShopcart(data?.addPayrollItemToShopCart);
      } else {
        notify('Ha ocurrido un error al intentar agregar al carrito', 'error');
      }
      router.push('/app/shopcart');
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
    } finally {
      setDisabled(false);
      NProgress.done();
    }
  };

  return (
    <>
      <Card className="shadow-sm">
        <Card.Body>
          {/* title */}
          <div>
            <h2 className="text-xl sm:text-3xl text-text-black">
              {product?.title}
            </h2>
            <h2 className="text-xl text-neutral-cold-400 mt-1 font-arial font-semibold">
              {company?.name}
            </h2>
          </div>
          {/* rating */}
          <ProductRating
            rating={product?.rating ?? 0}
            reviews={product?.reviews ?? 0}
          />
          <div
            className="text-black min-h-[8rem] flex items-center "
            dangerouslySetInnerHTML={{ __html: product?.description }}
          />
          <div className="w-full flex flex-col gap-2 pt-2 ">
            <VariantOptions
              product={product}
              selectedVariant={selectedVariant}
              setSelectedVariant={setSelectedVariant}
            />
          </div>
          {/* quantity and price */}
          <div className="flex my-4 flex-col sm:flex-row items-center gap-2">
            <div className="flex mx-auto gap-5 items-center mt-5 sm:mt-0">
              <span className="text-purple-300 text-xl font-extrabold">
                ${formatCurrency(selectedVariant?.price)}
              </span>
              <span className="text-neutral-cold-400  line-through">
                ${formatCurrency(selectedVariant?.compareAtPrice)}
              </span>
            </div>
          </div>
          {/* Buttons */}
          <div className="w-full flex flex-col sm:flex-row items-center gap-5">
            <Button
              className="order-2 sm:order-1"
              onClick={addPayrollItemToShopCart}
            >
              Agregar para todos los beneficiarios
            </Button>
            <div className="order-1 sm:order-2">
              <QuantityInput step={1} value={quantity} onChange={setQuantity} />
            </div>
          </div>
          <Button
            className="bg-neutral-warm-200 "
            onClick={(e) => {
              e.preventDefault();
              setModalIsOpen(true);
            }}
          >
            Asignar a beneficiario
          </Button>
        </Card.Body>
      </Card>
      <AddProductModal
        isOpen={modalIsOpen}
        setOpen={setModalIsOpen}
        product={product}
        variant={selectedVariant}
      />
    </>
  );
}

export default ProductInfo;
